import { Maximize2, X } from "lucide-react"
import { observer } from "mobx-react"
import { useEffect, useRef } from "react"
import { getTrayConfig } from "../../modules/registry"
import type { TrayManager } from "../../store/managers/TrayManager"

interface TaskDockedTrayProps {
    tray: TrayManager
}

export const TaskDockedTray = observer(function TaskDockedTray({ tray }: TaskDockedTrayProps): JSX.Element | null {
    const headerRef = useRef<HTMLElement>(null)
    const config = tray.openTray ? getTrayConfig(tray.openTray) : null
    useEffect(() => {
        if (config && tray.isDocked) headerRef.current?.focus()
    }, [config, tray.isDocked])
    if (!config?.renderContent || !tray.isDocked) return null
    const label = config.compactLabel ?? config.label

    return (
        <aside
            className="absolute inset-0 flex min-h-0 flex-col border-l border-border bg-base-100 text-base-content"
            aria-label={`${label} docked pane`}
            data-task-docked-tray="true"
        >
            <header
                ref={headerRef}
                className="relative z-20 flex h-11 shrink-0 items-center gap-2 border-b border-border bg-base-200 px-3 outline-none"
                tabIndex={-1}
            >
                <h2 className="min-w-0 flex-1 truncate text-xs font-semibold">{label}</h2>
                <div className="flex shrink-0 items-center gap-1" data-task-docked-controls="true">
                    <button
                        type="button"
                        className="btn flex h-8 shrink-0 items-center justify-center gap-1.5 border border-border bg-base-100 px-2 text-xs font-medium text-muted transition-colors hover:text-base-content"
                        onClick={() => tray.showModal()}
                        aria-label={`Open ${label} as a modal`}
                        title="Open as modal"
                    >
                        <Maximize2 size={13} />
                        <span>Modal</span>
                    </button>
                    <button
                        type="button"
                        className="btn flex h-8 shrink-0 items-center justify-center gap-1.5 border border-border bg-base-100 px-2 text-xs font-medium text-muted transition-colors hover:border-error/50 hover:text-error"
                        onClick={() => tray.close()}
                        aria-label={`Close ${label}`}
                        title={`Close ${label}`}
                    >
                        <X size={14} />
                        <span>Close</span>
                    </button>
                </div>
            </header>
            <div className="relative z-0 min-h-0 flex-1 overflow-hidden bg-base-100">{config.renderContent(tray, { compact: false })}</div>
        </aside>
    )
})
